import type { components } from "@/api-schema";
import { VehicleBuyInType } from "@/api-schema-enums";
import { DeviceType } from "@/common/hooks/use-device";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import type { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import type { ImplementedColumnDef } from "@/components/data-table/types";
import { PlaceholderLogo, PlaceholderLogoSize } from "@/components/placeholder-logo/placeholder-logo";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import { VehicleBuyInStatusBadge } from "@/components/vehicle-buy-in/vehicle-buy-in-status-badge";
import i18next from "i18next";
import type { ReactNode } from "react";
import { getVehicleBuyInTitle } from "./_helpers/get-vehicle-buy-in-title";

export type VehicleBuyInRow = components["schemas"]["VehicleBuyIn"] & RowWithIdentifier;

type VehicleBuyInCustomer = VehicleBuyInRow["documentSnapshot"]["customer"];

const VEHICLE_BUY_IN_TYPE_LABEL_KEYS = {
  [VehicleBuyInType.TradeIn]: "fab:VehicleBuyInTypeTradeIn",
  [VehicleBuyInType.Purchase]: "fab:VehicleBuyInTypePurchase",
} as const satisfies Record<VehicleBuyInType, string>;

const PLACEHOLDER_LOGO_SIZE_BY_VARIANT = {
  [BrandLogoVariant.COMPACT]: PlaceholderLogoSize.Sm,
  [BrandLogoVariant.MEDIUM]: PlaceholderLogoSize.Md,
  [BrandLogoVariant.DEFAULT]: PlaceholderLogoSize.Lg,
} as const satisfies Record<BrandLogoVariant, PlaceholderLogoSize>;

function getCustomerName(customer: VehicleBuyInCustomer): string {
  return customer.companyName || [customer.lastName, customer.firstName].filter(Boolean).join(", ");
}

function getEmployeeName(createdBy: VehicleBuyInRow["createdBy"]): string {
  return [createdBy?.firstName, createdBy?.lastName].filter(Boolean).join(" ");
}

function renderValue(value: string): ReactNode {
  return value || EMPTY_VALUE_PLACEHOLDER;
}

function renderVehicleBuyInType(type: VehicleBuyInRow["type"]): ReactNode {
  return type ? i18next.t(VEHICLE_BUY_IN_TYPE_LABEL_KEYS[type]) : EMPTY_VALUE_PLACEHOLDER;
}

function renderVehicle(buyIn: VehicleBuyInRow, variant = BrandLogoVariant.DEFAULT): ReactNode {
  const brand = buyIn.vehicle?.modelGeneration?.modelFamily?.brand;
  const snapshotBrandName = buyIn.documentSnapshot.vehicle.model.brandName.trim();
  const displayBrand = brand ?? (snapshotBrandName ? { name: snapshotBrandName } : undefined);

  return (
    <div className="flex items-start gap-3">
      {displayBrand ? (
        <BrandLogo brand={displayBrand} variant={variant} className="mt-0.5 shrink-0" />
      ) : (
        <PlaceholderLogo
          label={i18next.t("fab:UnknownBrand")}
          size={PLACEHOLDER_LOGO_SIZE_BY_VARIANT[variant]}
          className="mt-0.5 shrink-0"
        />
      )}
      <div className="min-w-0">
        <div className="font-medium wrap-break-word">
          {renderValue(getVehicleBuyInTitle(buyIn.vehicle, snapshotBrandName))}
        </div>
        <div className="text-muted-foreground truncate font-mono text-xs">
          {buyIn.vehicle?.vin || EMPTY_VALUE_PLACEHOLDER}
        </div>
      </div>
    </div>
  );
}

export const vehicleBuyInTableDefaultColumns: ImplementedColumnDef<VehicleBuyInRow, unknown>[] = [
  {
    id: "mobileSummary",
    size: 320,
    visibility: [DeviceType.Mobile],
    enableSorting: false,
    header: () => i18next.t("common:Vehicle"),
    cell: ({ row }) => {
      const buyIn = row.original;
      const { documentSnapshot } = buyIn;

      return (
        <div className="flex w-full flex-col gap-2">
          {renderVehicle(buyIn, BrandLogoVariant.COMPACT)}
          <div className="flex flex-wrap gap-2">
            <VehicleBuyInStatusBadge status={buyIn.status} />
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div>
              <dt className="text-muted-foreground">{i18next.t("fab:Customer")}</dt>
              <dd>{renderValue(getCustomerName(documentSnapshot.customer))}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("fab:VehicleBuyInType")}</dt>
              <dd>{renderVehicleBuyInType(buyIn.type)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("common:CreatedAt")}</dt>
              <dd>{automaticCellRenderer(buyIn.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("fab:Employee")}</dt>
              <dd>{renderValue(getEmployeeName(buyIn.createdBy))}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("fab:Branch")}</dt>
              <dd>{renderValue(buyIn.branch?.name ?? "")}</dd>
            </div>
          </dl>
        </div>
      );
    },
  },
  {
    id: "vehicleInfo",
    size: 240,
    visibility: [DeviceType.Tablet, DeviceType.Desktop],
    enableSorting: false,
    header: () => i18next.t("common:Vehicle"),
    cell: ({ row }) => renderVehicle(row.original),
  },
  {
    id: "vehicleDetails",
    size: 240,
    visibility: [DeviceType.Widescreen],
    enableSorting: false,
    header: () => i18next.t("common:Vehicle"),
    cell: ({ row }) => renderVehicle(row.original),
  },
  {
    id: "customer",
    accessorFn: (row) => getCustomerName(row.documentSnapshot.customer),
    size: 170,
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("fab:Customer"),
    cell: ({ row }) => renderValue(getCustomerName(row.original.documentSnapshot.customer)),
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    size: 130,
    visibility: [DeviceType.Tablet, DeviceType.Desktop],
    enableSorting: true,
    header: () => i18next.t("common:Status"),
    cell: ({ row }) => <VehicleBuyInStatusBadge status={row.original.status} />,
    align: "center",
  },
  {
    id: "employee",
    accessorFn: (row) => getEmployeeName(row.createdBy),
    size: 150,
    visibility: [DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("fab:Employee"),
    cell: ({ row }) => renderValue(getEmployeeName(row.original.createdBy)),
  },
  {
    id: "branch",
    accessorFn: (row) => row.branch?.name,
    size: 150,
    visibility: [DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("fab:Branch"),
    cell: ({ row }) => renderValue(row.original.branch?.name ?? ""),
  },
  {
    id: "type",
    accessorFn: (row) => row.type,
    size: 130,
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("fab:VehicleBuyInType"),
    cell: ({ row }) => renderVehicleBuyInType(row.original.type),
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    size: 150,
    visibility: [DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("common:Status"),
    cell: ({ row }) => <VehicleBuyInStatusBadge status={row.original.status} />,
  },
  {
    id: "createdAt",
    accessorFn: (row) => row.createdAt,
    size: 130,
    visibility: [DeviceType.Widescreen],
    enableSorting: true,
    header: () => i18next.t("common:CreatedAt"),
    cell: ({ row }) => {
      const date = automaticCellRenderer(row.original.createdAt, { dateFormat: "date" });
      const time = automaticCellRenderer(row.original.createdAt, { dateFormat: "time" });

      return (
        <div className="flex flex-col">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      );
    },
  },
];
